import { AcrValues, LoginEventPayload, OiamEvents, SetConfigEventPayload } from '@oiambk/oiam-oauth-types';
import { ERR_INVALID_CONFIG } from '../model/errors';
import { isAllowedCombination } from './acr-fns';
import { NginxErrorLogger } from './nginx-error-logger';
import { OauthEventService } from './oauth-event-service';
import { isValidUri } from './utils';

export class EServiceConfig {
  private _setConfigPayload: SetConfigEventPayload;
  private _forcedActingOnBehalfOf: string[];
  private _acrValues: AcrValues = {};
  private _customstate: string;
  private _accountLink: string;

  set setConfigPayload(payload: SetConfigEventPayload) {
    // validate config payload
    if (!this.checkConfig(payload)) {
      throw new Error('config error');
    }
    this._setConfigPayload = payload;
    // generate acr values from given config
    this.setLoginAcrValues(payload);
  }

  get setConfigPayload(): SetConfigEventPayload {
    return this._setConfigPayload;
  }

  set forcedActingOnBehalfOf(bafor: string[]) {
    this._forcedActingOnBehalfOf = bafor;
  }

  get forcedActingOnBehalfOf() {
    return this._forcedActingOnBehalfOf;
  }

  set acrValues(acr: AcrValues) {
    this._acrValues = acr;
  }

  get acrValues() {
    return this._acrValues;
  }

  get customstate() {
    return this._customstate;
  }

  get tdaUrl() {
    return this._setConfigPayload?.tdaUrl;
  }

  get accountLink() {
    return this._accountLink;
  }

  setLoginAcrValues(payload: LoginEventPayload) {
    if (!payload) {
      return;
    }
    this.setOrDeleteAcrValue('authnLevel', payload);
    this.setOrDeleteAcrValue('authnMethod', payload);
    this.setOrDeleteAcrValue('actingType', payload);
    this.setOrDeleteAcrValue('actingOnBehalfOf', payload);
    this.setOrDeleteAcrValue('forceProfileSelection', payload);
    this._customstate = payload.customstate ? payload.customstate : '';
    this._accountLink = payload.accountLink ? payload.accountLink : undefined;
  }

  mergeLoginAcrValues(payload: LoginEventPayload) {
    if (!payload) {
      return;
    }
    this.setAcrValue('authnLevel', payload);
    this.setAcrValue('authnMethod', payload);
    this.setAcrValue('actingType', payload);
    this.setAcrValue('actingOnBehalfOf', payload);
    this.setAcrValue('forceProfileSelection', payload);
    this._customstate = payload.customstate ? payload.customstate : this._customstate;
    this._accountLink = payload.accountLink ? payload.accountLink : this._accountLink;
  }

  /**
   * @returns the current requirements of the eService towards the JWT so that they can be
   * remembered as the previousInvocation.
   */
  public asPreviousInvocation(): LoginEventPayload {
    return {
      ...this.acrValues,
      ...(this.customstate ? { customstate: this.customstate } : undefined),
    };
  }

  private setOrDeleteAcrValue<T extends keyof AcrValues>(v: T, e: LoginEventPayload) {
    if (v in e) {
      if (e[v]) {
        this.acrValues[v] = e[v];
      } else {
        delete this.acrValues[v];
      }
    }
  }

  private setAcrValue<T extends keyof AcrValues>(v: T, e: LoginEventPayload) {
    if (v in e) {
      if (e[v]) {
        this.acrValues[v] = e[v];
      }
    }
  }

  private checkConfig(config: SetConfigEventPayload): boolean {
    if (!config) {
      this.logErrorAndDispatchEvent('missing SetConfigEventPayload');
      return false;
    }
    if (!config?.clientid) {
      this.logErrorAndDispatchEvent('missing client id');
      return false;
    }
    if (!config?.redirecturi) {
      this.logErrorAndDispatchEvent('missing redirect uri');
      return false;
    }
    if (!isValidUri(config?.redirecturi)) {
      this.logErrorAndDispatchEvent('redirect uri is not a valid url');
      return false;
    }
    if (!isAllowedCombination(config.actingType, config.actingOnBehalfOf)) {
      this.logErrorAndDispatchEvent('invalid combination of acting-type and acting-on-behalf-of');
      return false;
    }
    return true;
  }

  private logErrorAndDispatchEvent(errordetails: string) {
    const error = { ...ERR_INVALID_CONFIG, details: errordetails };
    NginxErrorLogger.logError(error);
    OauthEventService.doDispatchEvent(OiamEvents.ERROR, error);
  }
}
